/**
 * Field rows of «Зафиксировать» (design QuickCapture.dc.html): a 150px label column and the
 * control — combo, text, textarea, money with currency, date with quick picks, segmented, select.
 */
import { CURRENCIES } from '@outsource/shared';
import type { KeyboardEvent, ReactNode, Ref } from 'react';
import { cn } from '../../lib/cn';
import type { FieldSpec } from './captureFields';
import { firstOfMonth, firstOfNextMonth, listOf } from './captureForm';
import type { FormValues } from './captureForm';
import { ComboField } from './ComboField';
import { CalendarInput } from '../../components/ui/CalendarInput';

const INPUT = 'box-border h-[34px] rounded-2 border border-border-1 bg-bg-3 px-2.5 text-[13px] text-fg-1';

export interface FieldContext {
  readonly values: FormValues;
  readonly today: string;
  readonly set: (patch: Record<string, string>) => void;
  /** Enter in a text or money input saves (design `onFieldKey`). */
  readonly onEnter: () => void;
  readonly refFor: (key: string) => Ref<HTMLInputElement & HTMLTextAreaElement>;
}

const fieldId = (key: string) => `qc-${key}`;

function enterSaves(ctx: FieldContext) {
  return (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter' && !(event.metaKey || event.ctrlKey)) {
      event.preventDefault();
      ctx.onEnter();
    }
  };
}

function SegControl({
  spec,
  value,
  onChange,
}: {
  readonly spec: Extract<FieldSpec, { kind: 'seg' }>;
  readonly value: string;
  readonly onChange: (v: string) => void;
}) {
  const move = (event: KeyboardEvent<HTMLButtonElement>, index: number) => {
    const step =
      event.key === 'ArrowRight' || event.key === 'ArrowDown'
        ? 1
        : event.key === 'ArrowLeft' || event.key === 'ArrowUp'
          ? -1
          : 0;
    if (!step) return;
    event.preventDefault();
    const next = spec.options[(index + step + spec.options.length) % spec.options.length];
    if (next) onChange(next[0]);
    const group = event.currentTarget.parentElement;
    group
      ?.querySelectorAll<HTMLButtonElement>('[role="radio"]')
      [(index + step + spec.options.length) % spec.options.length]?.focus();
  };
  return (
    <div
      role="radiogroup"
      aria-label={spec.label}
      className="flex h-[34px] gap-0.5 self-start rounded-2 border border-border-1 bg-bg-2 p-0.5"
    >
      {spec.options.map(([option, label], index) => {
        const on = option === value;
        return (
          <button
            key={option}
            type="button"
            role="radio"
            aria-checked={on}
            tabIndex={on ? 0 : -1}
            onClick={() => onChange(option)}
            onKeyDown={(event) => move(event, index)}
            className={cn(
              'h-7 cursor-pointer whitespace-nowrap rounded-ctl border-0 px-3 text-[12px] font-semibold',
              on ? 'bg-bg-3 text-fg-1 shadow-seg' : 'bg-transparent text-fg-2',
            )}
          >
            {label}
          </button>
        );
      })}
    </div>
  );
}

function DateControl({ spec, ctx }: { readonly spec: FieldSpec; readonly ctx: FieldContext }) {
  const value = ctx.values[spec.key] ?? '';
  const quick = [
    ['Сегодня', ctx.today],
    ['1-е этого месяца', firstOfMonth(ctx.today)],
    ['1-е следующего', firstOfNextMonth(ctx.today)],
  ] as const;
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      <CalendarInput
        id={fieldId(spec.key)}
        ref={ctx.refFor(spec.key)}
        type="date"
        value={value}
        onChange={(e) => ctx.set({ [spec.key]: e.target.value })}
        className={INPUT}
      />
      {quick.map(([label, date]) => (
        <button
          key={label}
          type="button"
          aria-pressed={value === date}
          onClick={() => ctx.set({ [spec.key]: date })}
          className={cn(
            'h-7 flex-none cursor-pointer whitespace-nowrap rounded-pill border px-2.5 text-[12px] font-medium text-fg-1 hover:bg-bg-1',
            value === date ? 'border-pi-navy bg-pi-navy-tint' : 'border-border-1 bg-bg-3',
          )}
        >
          {label}
        </button>
      ))}
    </div>
  );
}

function MoneyControl({
  spec,
  ctx,
}: {
  readonly spec: Extract<FieldSpec, { kind: 'money' }>;
  readonly ctx: FieldContext;
}) {
  return (
    <div className="flex gap-1.5">
      <input
        id={fieldId(spec.key)}
        ref={ctx.refFor(spec.key)}
        value={ctx.values[spec.key] ?? ''}
        onChange={(e) => ctx.set({ [spec.key]: e.target.value })}
        onKeyDown={enterSaves(ctx)}
        inputMode="decimal"
        placeholder="0,00"
        className={cn(INPUT, 'w-[140px] text-right')}
      />
      <select
        aria-label="Валюта"
        value={spec.currency}
        onChange={(e) => ctx.set({ [`${spec.key}Cur`]: e.target.value })}
        className={cn(INPUT, 'px-2')}
      >
        {CURRENCIES.map((c) => (
          <option key={c} value={c}>
            {c}
          </option>
        ))}
      </select>
      {spec.unit && <span className="self-center text-fg-3">{spec.unit}</span>}
    </div>
  );
}

/** Several of a list as checkboxes (V234); the note after a label in grey («на ставке»). */
function ChecksControl({ spec, ctx }: { readonly spec: Extract<FieldSpec, { kind: 'checks' }>; readonly ctx: FieldContext }) {
  const picked = listOf(ctx.values[spec.key]);
  if (spec.options.length === 0) return <span className="pt-2 text-fg-3">{spec.empty}</span>;
  const toggle = (value: string) =>
    ctx.set({ [spec.key]: (picked.includes(value) ? picked.filter((v) => v !== value) : [...picked, value]).join(',') });
  return (
    <div role="group" aria-label={spec.label} className="flex flex-col gap-1.5 pt-2">
      {spec.options.map((option) => (
        <label key={option.value} className="flex cursor-pointer items-center gap-2">
          <input type="checkbox" checked={picked.includes(option.value)} onChange={() => toggle(option.value)} className="size-4 flex-none accent-pi-navy" />
          <span className="min-w-0 truncate">{option.label}</span>
          {option.note && <span className="flex-none text-[12px] text-fg-3">{option.note}</span>}
        </label>
      ))}
    </div>
  );
}

function Control({ spec, ctx }: { readonly spec: FieldSpec; readonly ctx: FieldContext }): ReactNode {
  const value = ctx.values[spec.key] ?? '';
  const id = fieldId(spec.key);
  switch (spec.kind) {
    case 'combo':
      return (
        <ComboField id={id} spec={spec} value={value} onChange={ctx.set} inputRef={ctx.refFor(spec.key)} />
      );
    case 'text':
      return (
        <input
          id={id}
          ref={ctx.refFor(spec.key)}
          value={value}
          placeholder={spec.placeholder}
          onChange={(e) => ctx.set({ [spec.key]: e.target.value })}
          onKeyDown={enterSaves(ctx)}
          className={cn(INPUT, 'w-full')}
        />
      );
    case 'area':
      return (
        <textarea
          id={id}
          ref={ctx.refFor(spec.key)}
          value={value}
          rows={3}
          placeholder={spec.placeholder}
          onChange={(e) => ctx.set({ [spec.key]: e.target.value })}
          className="box-border w-full resize-y rounded-2 border border-border-1 bg-bg-3 px-2.5 py-2 font-sans text-[13px] leading-[1.5] text-fg-1"
        />
      );
    case 'money':
      return <MoneyControl spec={spec} ctx={ctx} />;
    case 'date':
      return <DateControl spec={spec} ctx={ctx} />;
    case 'seg':
      return (
        <SegControl
          spec={spec}
          value={value || spec.current || (spec.options[0]?.[0] ?? '')}
          onChange={(v) => ctx.set({ [spec.key]: v })}
        />
      );
    case 'checks':
      return <ChecksControl spec={spec} ctx={ctx} />;
    case 'select':
      return (
        <select
          id={id}
          value={value}
          onChange={(e) => ctx.set({ [spec.key]: e.target.value, ...(spec.onPick?.(e.target.value) ?? {}) })}
          className={cn(INPUT, 'max-w-full')}
        >
          {spec.options.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
      );
  }
}

export function FieldRow({ spec, ctx }: { readonly spec: FieldSpec; readonly ctx: FieldContext }) {
  const labelled = spec.kind !== 'seg' && spec.kind !== 'checks';
  return (
    <div className="grid grid-cols-[150px_minmax(0,1fr)] items-start gap-3">
      {labelled ? (
        <label htmlFor={fieldId(spec.key)} className="pt-2 font-medium text-fg-2">
          {spec.label}
          {spec.required && <RequiredMark />}
        </label>
      ) : (
        <span className="pt-2 font-medium text-fg-2">{spec.label}</span>
      )}
      <div className="flex min-w-0 flex-col gap-1.5">
        <Control spec={spec} ctx={ctx} />
        {spec.hint && <span className="text-[12px] text-fg-3">{spec.hint}</span>}
      </div>
    </div>
  );
}

/** The design's « *», plus text for screen readers (accessibility rule: not only a symbol). */
function RequiredMark() {
  return (
    <>
      <span aria-hidden="true" className="text-fg-3">
        {' '}
        *
      </span>
      <span className="sr-only"> (обязательно)</span>
    </>
  );
}
